// Campaign types based on backend schema
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'completed';
export type CampaignType = 'standard' | 'premium' | 'sponsored';
export type AdDisplayType = 'banner' | 'video_overlay' | 'third_party' | 'l_banner' | 'square_banner' | 'bottom_banner' | 'full_page';
export type ImpressionLimitType = 'daily_campaign' | 'total_campaign' | 'daily_user' | 'weekly_user';
export type MatchType = 'exact' | 'phrase' | 'broad' | 'semantic';
export type ContentType = 'movie' | 'series' | 'documentary' | 'live';
export type IncludeExclude = 'include' | 'exclude';

// Base campaign data interface
export interface CampaignData {
  name: string;
  description?: string;
  campaignType: CampaignType;
  status?: CampaignStatus;
  startDate?: string;
  endDate?: string;
  budget?: number;
  currency?: string;
  targetingSettings?: Record<string, any>;
  frequencyCap?: Record<string, any>;
  autoScheduling?: AutoSchedulingConfig;
  validationRules?: Record<string, any>;
  enhancedFeatures?: Record<string, any>;
}

// Enhanced campaign interface with all data
export interface Campaign extends CampaignData {
  id: string;
  tenantId: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  // Additional computed fields
  impressions?: number;
  clicks?: number;
  spent?: number;
  ctr?: number;
  cpm?: number;
  targetKeywords?: (TargetKeyword & { id: string; status: 'active' | 'paused' })[];
  adCreatives?: AdCreative[];
  adFormat?: string;
}

// Auto scheduling configuration
export interface AutoSchedulingConfig {
  autoStart?: boolean;
  autoPause?: {
    enabled: boolean;
    budgetThreshold?: number;
    impressionThreshold?: number;
  };
  autoStop?: {
    enabled: boolean;
    conditions?: Record<string, any>;
  };
}

// Impression limits
export interface ImpressionLimits {
  dailyCampaign?: number;
  totalCampaign?: number;
  dailyUser?: number;
  weeklyUser?: number;
}

export interface ImpressionLimit {
  id?: string;
  campaignId: string;
  limitType: ImpressionLimitType;
  limitValue: number;
  currentCount?: number;
  resetAt?: string;
}

// Targeting interfaces
export interface NegativeKeyword {
  id?: string;
  campaignId?: string;
  keyword: string;
  matchType: MatchType;
  status?: 'active' | 'paused';
}

export interface NegativeBrand {
  id?: string;
  campaignId?: string;
  brandName: string;
  brandAliases?: string[];
  status?: 'active' | 'paused';
}

export interface SceneTargeting {
  id?: string;
  campaignId?: string;
  sceneType: string;
  confidenceThreshold: number;
  includeExclude: IncludeExclude;
  status?: 'active' | 'paused';
}

export interface ContentTypeTargeting {
  id?: string;
  campaignId?: string;
  contentType: ContentType;
  includeExclude: IncludeExclude;
  status?: 'active' | 'paused';
}

export interface SpecificContentTargeting {
  id?: string;
  campaignId?: string;
  contentId: string;
  includeExclude: IncludeExclude;
  status?: 'active' | 'paused';
}

export interface ContentGroupTargeting {
  id?: string;
  campaignId?: string;
  groupName: string;
  includeExclude: IncludeExclude;
  status?: 'active' | 'paused';
}

// Ad creative interfaces
export interface AdCreativeData {
  name: string;
  adType: AdDisplayType;
  displayType?: AdDisplayType;
  title?: string;
  description?: string;
  imageUrl?: string;
  videoUrl?: string;
  clickUrl?: string;
  adServerTag?: string;
  creativeData?: Record<string, any>;
  trackingPixels?: string[];
  impressionTrackingUrls?: string[];
  clickTrackingUrls?: string[];
  layoutConfig?: Record<string, any>;
  responsiveConfig?: Record<string, any>;
  dimensions?: {
    width: number;
    height: number;
    aspectRatio?: string;
  };
}

export interface AdCreative extends AdCreativeData {
  id: string;
  campaignId: string;
  status: 'active' | 'paused';
  createdAt: string;
  updatedAt: string;
}

// Campaign validation
export interface CampaignValidation {
  id?: string;
  campaignId: string;
  validationType: 'budget' | 'dates' | 'creatives' | 'targeting' | 'general';
  isValid: boolean;
  errors: string[];
  warnings: string[];
  validatedAt: string;
  validatedBy?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Target keyword interface
export interface TargetKeyword {
  keyword: string;
  matchType: MatchType;
  priority: number;
  bidAmount?: number;
  matchCount?: number;
  matchDetails?: KeywordMatchDetail[];
}

// Keyword match detail interface
export interface KeywordMatchDetail {
  contentId: string;
  contentTitle: string;
  startTime: number;
  endTime: number;
  confidence: number;
}

// Campaign filters for API
export interface CampaignFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: CampaignStatus;
  campaignType?: CampaignType;
  displayType?: AdDisplayType;
  search?: string;
  startDateFrom?: string;
  startDateTo?: string;
  budgetMin?: number;
  budgetMax?: number;
  hasImpressionLimits?: boolean;
  includeMetrics?: boolean;
}

// API response interfaces
export interface CampaignListResponse {
  campaigns: Campaign[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: Record<string, any>;
}

export interface CampaignResponse {
  success: boolean;
  data?: Campaign;
  error?: string;
  message?: string;
}

// Form data interfaces
export interface CampaignFormData extends CampaignData {
  targetKeywords?: string[];
  impressionLimits?: ImpressionLimits;
  negativeKeywords?: string[]; // Simplified for form data
  negativeBrands?: NegativeBrand[];
  sceneTargeting?: SceneTargeting[];
  contentTypeTargeting?: ContentTypeTargeting[];
  specificContentTargeting?: SpecificContentTargeting[];
  contentGroupTargeting?: ContentGroupTargeting[];
  adCreatives?: AdCreativeData[];
}

// Content interface for targeting
export interface Content {
  id: string;
  title: string;
  description?: string;
  contentType: ContentType;
  status: string;
  createdAt: string;
}

// File upload interfaces
export interface FileUploadResponse {
  uploadUrl: string;
  fileUrl: string;
}

// Display type configurations
export interface DisplayTypeConfig {
  name: string;
  description: string;
  dimensions: {
    width: number;
    height: number;
    aspectRatio: string;
  };
  requirements: string[];
  supportedFormats: string[];
}

export const DISPLAY_TYPE_CONFIGS: Record<AdDisplayType, DisplayTypeConfig> = {
  banner: {
    name: 'Standard Banner',
    description: 'Traditional banner ad displayed over content',
    dimensions: { width: 728, height: 90, aspectRatio: '8:1' },
    requirements: ['Image URL required'],
    supportedFormats: ['JPG', 'PNG', 'GIF']
  },
  l_banner: {
    name: 'L-Banner',
    description: 'L-shaped banner positioned at corner',
    dimensions: { width: 300, height: 250, aspectRatio: '6:5' },
    requirements: ['Image URL required'],
    supportedFormats: ['JPG', 'PNG', 'GIF']
  },
  square_banner: {
    name: 'Square Banner',
    description: 'Square format banner ad',
    dimensions: { width: 250, height: 250, aspectRatio: '1:1' },
    requirements: ['Image URL required'],
    supportedFormats: ['JPG', 'PNG', 'GIF']
  },
  bottom_banner: {
    name: 'Bottom Banner',
    description: 'Banner displayed at bottom of screen',
    dimensions: { width: 320, height: 50, aspectRatio: '32:5' },
    requirements: ['Image URL required'],
    supportedFormats: ['JPG', 'PNG', 'GIF']
  },
  video_overlay: {
    name: 'Video Overlay',
    description: 'Video ad overlaid on content',
    dimensions: { width: 640, height: 360, aspectRatio: '16:9' },
    requirements: ['Video URL required'],
    supportedFormats: ['MP4', 'WebM']
  },
  third_party: {
    name: 'Third Party',
    description: 'External ad server integration',
    dimensions: { width: 0, height: 0, aspectRatio: 'flexible' },
    requirements: ['Ad server tag required'],
    supportedFormats: ['HTML', 'JavaScript']
  },
  full_page: {
    name: 'Full Page',
    description: 'Full screen interstitial ad',
    dimensions: { width: 1920, height: 1080, aspectRatio: '16:9' },
    requirements: ['Image or Video URL required'],
    supportedFormats: ['JPG', 'PNG', 'MP4', 'WebM']
  }
};

// Analytics interfaces
export interface DailyAnalytics {
  date: string;
  impressions: number;
  clicks: number;
  ctr: number;
  spend: number;
}

export interface CampaignAnalyticsResponse {
  success: boolean;
  data: {
    totalImpressions: number;
    totalClicks: number;
    averageCTR: number;
    totalSpend: number;
    dateRange: {
      startDate: string;
      endDate: string;
    };
    dailyData: DailyAnalytics[];
  };
  message?: string;
}